import { useState } from "react";
import { ChevronRight, FileText, Pencil, Terminal, Search, Check, X, Loader2 } from "lucide-react";
import type { ContentBlock } from "@/types/content-blocks";

type ToolUseData = Extract<ContentBlock, { type: "tool_use" }>;

function getToolIcon(toolName: string) {
  const name = toolName.toLowerCase();
  if (name.includes("read") || name.includes("search") || name.includes("glob") || name.includes("grep"))
    return <Search className="w-3 h-3 text-blue-400" />;
  if (name.includes("edit") || name.includes("write"))
    return <Pencil className="w-3 h-3 text-amber-400" />;
  if (name.includes("bash") || name.includes("command") || name.includes("exec"))
    return <Terminal className="w-3 h-3 text-emerald-400" />;
  return <FileText className="w-3 h-3 text-zinc-400" />;
}

function StatusIcon({ status }: { status: ToolUseData["status"] }) {
  switch (status) {
    case "running":
      return <Loader2 className="w-3 h-3 animate-spin text-amber-400" />;
    case "done":
      return <Check className="w-3 h-3 text-emerald-400" />;
    case "error":
      return <X className="w-3 h-3 text-red-400" />;
  }
}

interface Props {
  block: ToolUseData;
}

export function ToolUseBlock({ block }: Props) {
  const [expanded, setExpanded] = useState(false);
  const summary = block.input.summary as string | undefined ?? "";

  return (
    <div className="my-1 rounded-md border border-white/[0.06] bg-[#151518] overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-white/[0.03] transition-colors cursor-pointer"
      >
        <ChevronRight
          className={`w-3 h-3 text-zinc-500 transition-transform ${expanded ? "rotate-90" : ""}`}
        />
        {getToolIcon(block.toolName)}
        <span className="text-[12px] font-mono text-zinc-300">{block.toolName}</span>
        <span className="text-[11px] font-mono text-zinc-500 truncate flex-1 min-w-0">{summary}</span>
        <StatusIcon status={block.status} />
      </button>

      {expanded && block.result && (
        <div className="px-3 pb-2 border-t border-white/[0.04]">
          <pre className="mt-2 text-[11px] font-mono text-zinc-400 whitespace-pre-wrap max-h-[300px] overflow-y-auto" style={{ scrollbarWidth: "thin", scrollbarColor: "#2A2A2F transparent" }}>
            {block.result.output || "(no output)"}
          </pre>
        </div>
      )}

      {expanded && !block.result && block.status === "running" && (
        <div className="px-3 pb-2 border-t border-white/[0.04]">
          <span className="text-[11px] text-zinc-500">Running...</span>
        </div>
      )}
    </div>
  );
}
